import React, { useMemo } from 'react';
import { Page, Text, View, Document, StyleSheet, Font } from '@react-pdf/renderer';

// --- Helper Functions (Reused) ---

const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isNumericHeader = (header) => {
    // Explicitly exclude descriptive keys from being formatted as numbers
    if (header.toUpperCase().includes('CODE') || header.toUpperCase().includes('NAME')) {
        return false;
    }
    
    // Check if it contains common numeric keywords (e.g., SALES, RETURN, VAT, AMT, %)
    return ['SALES', 'RETURN', 'VAT', 'COMMISSION', 'NET', '%', 'AMT'].some(s => header.toUpperCase().includes(s));
};

// Define the two fixed, sticky descriptive columns
const PRIMARY_STICKY_KEY = 'GRPCODE';
const SECONDARY_STICKY_KEY = 'GROUPNAME';
const STICKY_COLUMNS = [PRIMARY_STICKY_KEY, SECONDARY_STICKY_KEY];
const FOOTER_TOTAL_KEYS = [
    'CASH SALES', 
    'NETCASH SALES', 
    'NETCASH SALESRETURN',
    'CREDIT SALES', 
    'NETCREDIT SALES', 
    'NETCREDIT SALESRETURN', 
    'SALES RETURN', 
    'NETSALES RETURN', 
    'VAT AMT', 
    'COMMISSION', 
    'NET SALES',
    'NET SALES EXCLVAT'
];


/**
 * Calculates percentage widths for all columns based on defined weights.
 * Primary sticky column (GRPCODE) gets 1.5x weight. Secondary (GROUPNAME) gets 2.5x. Data columns get 1x.
 */
const getColumnWidths = (dataColumnsCount) => {
    const primaryStickyWeight = 1.5;
    const secondaryStickyWeight = 2.5;
    const standardWeight = 1;
    
    const totalWeight = primaryStickyWeight + secondaryStickyWeight + (dataColumnsCount * standardWeight);
    
    const primaryStickyWidth = (primaryStickyWeight / totalWeight) * 100;
    const secondaryStickyWidth = (secondaryStickyWeight / totalWeight) * 100;
    const standardWidth = (standardWeight / totalWeight) * 100;

    return {
        primaryStickyWidth: `${primaryStickyWidth.toFixed(2)}%`,
        secondaryStickyWidth: `${secondaryStickyWidth.toFixed(2)}%`,
        standardWidth: `${standardWidth.toFixed(2)}%`,
    };
};


// --- Styling Definitions ---

const BORDER_COLOR = '#999'; 
const BORDER_WIDTH = 1;
const HEADER_COLOR = '#e0f7fa'; // Header background
const FOOTER_COLOR = '#80cbc4'; // Footer background

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#fff',
    padding: 20,
  },
  title: {
    fontSize: 14,
    marginBottom: 10,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  // Table Structure
  table: { 
    display: 'table', 
    width: '100%', 
    // This is the overall table border
    // borderStyle: 'solid',
    // borderWidth: BORDER_WIDTH,
    // borderColor: BORDER_COLOR,
    // borderRightWidth: 0,
    // borderBottomWidth: 0,
  },
  tableRow: { 
    flexDirection: 'row', 
    width: '100%', 
    alignItems: 'stretch', // ensures cells fill row height
  },
  tableHeader: { 
    backgroundColor: HEADER_COLOR,
    fontWeight: 'bold',
  },
  // Base Cell Styles
  tableCell: { 
    // borderColor: BORDER_COLOR,
    // borderStyle: 'solid', 
    // // Cell borders are only needed on the right and bottom to complete the grid
    // borderBottomWidth: BORDER_WIDTH,
    // borderRightWidth: BORDER_WIDTH,
    // borderLeftWidth: 0, 
    // borderTopWidth: 0,
    
    padding: 4, 
    fontSize: 7, 
  },
  // Specific Cell Overrides
  descriptiveCell: {
    textAlign: 'left',
    // Apply left border only to the first cell in the row
   // borderLeftWidth: BORDER_WIDTH, 
  },
  numericCell: {
    textAlign: 'right',
  },
  // Footer Styles
  tableFooterRow: { 
    flexDirection: 'row', 
    width: '100%',
    backgroundColor: FOOTER_COLOR,
    // borderTopWidth: 3, 
    // borderTopColor: '#000', 
  },
  footerCell: {
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'right',
    padding: 6,
    // Base footer cells should match the row's top border but remove others
    // borderStyle: 'solid',
    // borderColor: BORDER_COLOR,
    // borderWidth: 0,
  }
});


// --- PDF Component Content ---
const SalesReportContent = ({ rows }) => { 

    // 1. Determine the non-sticky (remaining) display columns
    const remainingDisplayKeys = useMemo(() => {
        if (rows.length === 0) return [];
        
        const allKeys = Object.keys(rows[0]);
        
        return allKeys.filter(key =>
            !STICKY_COLUMNS.includes(key) &&
            !['isGroupHeader', 'isSubHeader', 'isSubSubHeader', 'totalAmount', 'totalAmountExclVAT', 'YEAR'].includes(key)
        );
    }, [rows]);
    
    // 2. Calculate FIXED WIDTHS
    const { primaryStickyWidth, secondaryStickyWidth, standardWidth } = getColumnWidths(remainingDisplayKeys.length);

    // 3. Calculate the Grand Totals internally
    const internalGrandTotals = useMemo(() => {
        if (rows.length === 0) return {};
        const keysToTotal = remainingDisplayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
        const totals = {};
        for (const key of keysToTotal) {
            totals[key] = rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
        }
        return totals;
    }, [rows, remainingDisplayKeys]);
    
    const totalValueKeys = remainingDisplayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));

    // 4. Calculate Footer Span Width (based on the index where totals start)
    let totalColumnsWidth = 0;
    const totalColumns = STICKY_COLUMNS.length + remainingDisplayKeys.length;
    
    if (rows.length > 0) {
        // Find the index in remainingDisplayKeys where the totals start
        const firstTotalKey = totalValueKeys[0];
        const firstTotalIndexInRemaining = remainingDisplayKeys.indexOf(firstTotalKey);

        // Calculate the total width of the descriptive span
        if (firstTotalIndexInRemaining === -1) {
            // No totals, span entire table width
            totalColumnsWidth = 100;
        } else {
            // Total width = Primary + Secondary + (Non-total data columns * Standard Width)
            totalColumnsWidth = parseFloat(primaryStickyWidth) + 
                                parseFloat(secondaryStickyWidth) + 
                                (firstTotalIndexInRemaining * parseFloat(standardWidth));
        }
    }
    const totalDescriptiveWidth = `${totalColumnsWidth.toFixed(2)}%`;

    if (rows.length === 0) return <Text style={{ textAlign: 'center' }}>No sales data found for the selected filters.</Text>;

    return (
        <View style={styles.table}>
            {/* Header Row */}
            <View style={[styles.tableRow, styles.tableHeader]}>
                {/* 1. Sticky Headers (Fixed Widths) */}
                <Text style={[styles.tableCell, styles.descriptiveCell, { width: primaryStickyWidth }]}>
                    {PRIMARY_STICKY_KEY.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                </Text>
                <Text style={[styles.tableCell, styles.descriptiveCell, { width: secondaryStickyWidth }]}>
                    {SECONDARY_STICKY_KEY.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                </Text>
                {/* 2. Dynamic Headers (Standard Width) */}
                {remainingDisplayKeys.map(key => (
                    <Text
                        key={key}
                        style={[
                            styles.tableCell, 
                            isNumericHeader(key) ? styles.numericCell : styles.descriptiveCell,
                            { width: standardWidth }
                        ]}
                    >
                        {key.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                    </Text>
                ))}
            </View>

            {/* Body Rows */}
            {rows.map((row, index) => (
                <View key={`agg-group-row-${index}`} style={styles.tableRow}>
                    
                    {/* 1. Sticky Cells (Fixed Widths) */}
                    <Text style={[styles.tableCell, styles.descriptiveCell, { width: primaryStickyWidth, backgroundColor: '#ffffff' }]}>
                        {row[PRIMARY_STICKY_KEY] || 'N/A'}
                    </Text>
                    <Text style={[styles.tableCell, styles.descriptiveCell, { width: secondaryStickyWidth, backgroundColor: '#ffffff' }]}>
                        {row[SECONDARY_STICKY_KEY] || 'N/A'}
                    </Text>
                    
                    {/* 2. Data Cells (Standard Width) */}
                    {remainingDisplayKeys.map(key => (
                        <Text 
                            key={key} 
                            style={[
                                styles.tableCell, 
                                isNumericHeader(key) ? styles.numericCell : styles.descriptiveCell,
                                { width: standardWidth }
                            ]}
                        >
                            {isNumericHeader(key) ? formatNumber(row[key]) : row[key]}
                        </Text>
                    ))}
                </View>
            ))}

            {/* Footer - Grand Totals */}
            {Object.keys(internalGrandTotals).length > 0 && (
                <View style={styles.tableFooterRow}>
                    {/* 1. GRAND TOTAL label cell (Spanning width) */}
                    <Text style={[
                        styles.footerCell, 
                        { 
                            width: totalDescriptiveWidth,
                            // Ensure borders are drawn for the merged cell
                            // borderLeftWidth: BORDER_WIDTH,
                            // borderRightWidth: BORDER_WIDTH, 
                            // borderColor: BORDER_COLOR,
                            textAlign: 'right',
                        }
                    ]}>
                        GRAND TOTAL:
                    </Text>
                    
                    {/* 2. Total cells (Standard Width) */}
                    {remainingDisplayKeys.slice(totalColumns - (remainingDisplayKeys.length - (totalDescriptiveWidth - parseFloat(primaryStickyWidth) - parseFloat(secondaryStickyWidth)) / parseFloat(standardWidth))).map((key) => {
                         const isTotalKey = FOOTER_TOTAL_KEYS.includes(key);
                        
                        return (
                            <Text 
                                key={key} 
                                style={[
                                    styles.footerCell, 
                                    styles.numericCell, 
                                    { 
                                        width: standardWidth,
                                        // Draw right border
                                        // borderRightWidth: BORDER_WIDTH,
                                        // borderColor: BORDER_COLOR,
                                    } 
                                ]}
                            >
                                {isTotalKey ? formatNumber(internalGrandTotals[key]) : ''}
                            </Text>
                        );
                    })}
                </View>
            )}
        </View>
    );
};

// Wrapper Document Component
const SalesTableCustomerGroupwiseAggregatedPDF = ({ rows, reportTitle = "Sales Groupwise Aggregated Report" }) => (
  <Document>
    {console.log("SalesTableCustomerGroupwiseAggregatedPDF")}
    <Page size="A4" style={styles.page} orientation="landscape">
        <Text style={styles.title}>{reportTitle}</Text>
        <SalesReportContent rows={rows} />
    </Page>
  </Document>
);

export default SalesTableCustomerGroupwiseAggregatedPDF;